/**
 * Typed hooks per resource.
 *
 * One file today because there are three resources. When `catalog` and `orders`
 * land this splits per module, mirroring the backend layout — the frontend's
 * boundaries should be recognisable to someone who knows the API's.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAttemptKeys } from './attempt';
import type { ApiError } from './client';
import { useApi } from './provider';

export interface Location {
  id: string;
  name: string;
  code: string;
  timezone: string;
  currency: string;
  /** How numbers, money and dates are written here: "fr-FR", "en-US". */
  locale: string;
  business_day_cutoff: string;
  is_active: boolean;
}

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  status: string;
  default_currency: string;
  /** ISO 3166-1 alpha-2. */
  country: string;
  default_locale: string;
  /**
   * Whether any sales are recorded. The currency can still change; this is
   * what lets a screen warn that past figures will not be converted.
   */
  sales_recorded: boolean;
}

export interface Plan {
  key: string;
  name: string;
  tier: number;
  price_month: string | null;
  price_year: string | null;
}

export interface CreateLocation {
  name: string;
  code: string;
  timezone?: string;
  currency?: string;
}

/**
 * The sites, with each one's currency and number format.
 *
 * Refetched every few minutes as well as on focus: a till bolted to a bar is
 * never refocused, and a change made in Settings should reach it within the
 * hour's first order rather than the next reboot.
 */
export function useLocations() {
  const api = useApi();
  return useQuery({
    queryKey: ['locations'],
    queryFn: ({ signal }) => api.get<Location[]>('/v1/locations', signal),
    refetchInterval: 5 * 60_000,
  });
}

export function useTenant() {
  const api = useApi();
  return useQuery({
    queryKey: ['tenant'],
    queryFn: ({ signal }) => api.get<Tenant>('/v1/tenant', signal),
  });
}

export function usePlans() {
  const api = useApi();
  return useQuery({
    queryKey: ['plans'],
    queryFn: ({ signal }) => api.get<Plan[]>('/v1/plans', signal),
    staleTime: 60 * 60_000,
  });
}

// `useRoles` lived here and moved to `roles.ts` when roles gained permissions
// and an editor. The type here had no `permissions` field, so a screen built
// on it would have shown role names and silently dropped the only thing on the
// page worth reading — and an hour-long `staleTime` would have gone on showing
// the old permissions after somebody changed them.

export function useCreateLocation() {
  const api = useApi();
  const queryClient = useQueryClient();

  const attempts = useAttemptKeys();

  return useMutation<Location, ApiError, CreateLocation>({
    mutationFn: (payload) =>
      api.post<Location>('/v1/locations', payload, {
        idempotencyKey: attempts.keyFor(payload),
      }),
    onSuccess: (_location, payload) => {
      attempts.succeeded(payload);
      void queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}

/**
 * A till, on the books.
 *
 * `Terminal` was a table nothing could write to and `terminal_id` was accepted
 * on every payment with nothing behind it, so a till with a typo in its setup
 * became a terminal that did not exist and produced a drawer report that
 * reconciled against nothing.
 */
export interface Terminal {
  id: string;
  location_id: string;
  name: string;
  /**
   * Whatever the till reports about itself — a serial, a browser fingerprint,
   * an MDM identifier. Never trusted for anything: it is here so an engineer
   * standing in front of a broken till can tell which row is it.
   */
  device_fingerprint: string | null;
  is_active: boolean;
}

/**
 * The tills at a location. Retired ones are hidden unless asked for.
 *
 * Hidden rather than gone: every payment ever taken on a retired terminal
 * still points at it, so a reconciliation report for a past month has to be
 * able to name it. Ask for them when showing history, leave them out of a
 * picker.
 */
export function useTerminals(locationId: string | undefined, includeInactive = false) {
  const api = useApi();
  return useQuery({
    queryKey: ['terminals', locationId ?? null, includeInactive],
    queryFn: ({ signal }) =>
      api.get<Terminal[]>(
        `/v1/terminals?location_id=${locationId}&include_inactive=${includeInactive}`,
        signal,
      ),
    enabled: Boolean(locationId),
    // Tills are registered at setup and then sit there for years.
    staleTime: 10 * 60_000,
  });
}

/**
 * Put a till on the books. Counts against `terminals.max`.
 *
 * `name` is what somebody says out loud — "Bar 2", "Kitchen pass" — because it
 * ends up in a reconciliation report a manager reads at midnight, where a
 * serial number is no help at all. Two tills at one location cannot share one.
 */
export function useRegisterTerminal() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Terminal,
    ApiError,
    { locationId: string; name: string; deviceFingerprint?: string }
  >({
    mutationFn: (input) =>
      api.post<Terminal>('/v1/terminals', {
        location_id: input.locationId,
        name: input.name,
        device_fingerprint: input.deviceFingerprint ?? null,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['terminals'] });
    },
  });
}

/**
 * Take a till out of service. It stops counting against the plan.
 *
 * Not a delete, and worth saying so on the button: the payments taken on it
 * still point here. "Retire" rather than "Remove", and no destructive
 * confirmation language — nothing is being destroyed.
 */
export function useRetireTerminal() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Terminal, ApiError, { terminalId: string }>({
    mutationFn: ({ terminalId }) => api.post<Terminal>(`/v1/terminals/${terminalId}/retire`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['terminals'] });
    },
  });
}

export interface RegionInput {
  country?: string;
  currency?: string;
  locale?: string;
}

/**
 * Set the restaurant's country, currency and number format. Every site follows.
 *
 * The currency can change at any time; nothing already recorded is converted.
 * 409 with `field` for a code that does not exist.
 */
export function useUpdateRegion() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Tenant, ApiError, RegionInput>({
    mutationFn: (input) =>
      api.request<Tenant>('/v1/tenant/region', { method: 'PATCH', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant'] });
      void queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}

export interface RegionSuggestion {
  country: string;
  currency: string | null;
  locale: string | null;
}

/** The currency and format a restaurant in this country most likely wants. */
export function useRegionSuggestion(country: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['tenant', 'region-suggestion', country],
    queryFn: ({ signal }) =>
      api.get<RegionSuggestion>(`/v1/tenant/region/suggestion?country=${country}`, signal),
    enabled: Boolean(country),
    staleTime: Infinity,
  });
}
